import * as React from "react";
import {
  Dispatch,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useHistory, useParams } from "react-router";
import { styled } from "@mui/material";
import { useStoredPatientCase } from "./useStoredPatientCase";
import { usePatientGraph } from "fe-web-components/diseaseview/hooks";
import {
  GraphDetailSelection,
  useGraphDetailSelection,
} from "../../util/graphDetialsSelectionHook";
import Paper from "@mui/material/Paper";
import { PatientDiseaseInfo } from "./PatientDiseaseInfo";
import {
  FindingStrength,
  useCalculateFindingPredictives,
} from "../../util/FindingStrengthCalculator";
import { DDClinicalReasoning } from "fe-common/graphql/dao";
import { ReferencesView } from "./ReferencesView";
import { GraphAreaActions } from "fe-web-components/graphview";
import { QualifiedFeature } from "fe-common/graphql";
import { ACTION, CATEGORY, evt } from "fe-common/analytics/analytics";
import { hashQF } from "fe-common/logic";

import { scrollableStyle } from "fe-common/ui/commonStyles";
import { DiseaseCluster } from "fe-common/hooks/sortFeaturesWithCrs";
import { getArticles } from "../copiedGraphDetails";
import { useDiseasesSections } from "./hooks/useDiseasesSections";
import { GraphSelectionSummary } from "../graphSelectionSummary";
import { useQueryParams } from "fe-web-components/useQueryParams";
import { useScreenSize } from "./util/useScreenSize";
import { NavBarZIndex, StackingLevel } from "fe-common/ui/stacking";
import DesktopExpandView from "../../themeComponents/DesktopExpandView";
import Graph from "../diagnosticAgent/graph";
import { ThreeDotsLoader } from "fe-web-components/loader/threeDotsLoader";
import { KahunColorMap } from "fe-common/ui/assets/colorMap";
import Button from "@mui/material/Button";
import ClinicalReasoningIconSVG from "../../assets/clinicalReasoningIcon.svg";
import { BackButton } from "./common/backButton";
import { usePatientCase } from "./usePatientCase";
import { patientCaseByPresentationType } from "./updateDDToState";

//region [[ Styles ]]
const GraphContainerMobile = styled("div")({
  display: "flex",
  flex: 1,
  height: "calc(100vh - 50px)",
});

const LeftCardContainer = styled(Paper)(({ theme }) => ({
  borderTop: "unset",
  borderBottom: "unset",
  borderLeft: "unset",
  borderTopLeftRadius: "20px",
  borderBottomLeftRadius: "20px",
  borderTopRightRadius: "unset",
  borderBottomRightRadius: "unset",
  padding: "20px 30px",
  width: "370px",
  minWidth: "370px",
  borderRightWidth: "1px",
  borderRightStyle: "solid",
  borderRightColor: "rgb(218, 220, 224)",
  ...scrollableStyle,
}));

const GraphSelectionSummaryMobile = styled(GraphSelectionSummary)({
  position: "relative",
  backgroundColor: "white",
  padding: "10px",
  fontSize: "14px",
  color: KahunColorMap.DefaultLight,
  margin: "unset",
  width: "100%",
});

const MobileBottomArea = styled("div")({
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  position: "fixed",
  bottom: 0,
  borderTopRightRadius: "25px",
  borderTopLeftRadius: "25px",

  boxShadow: "0px -2px 8px rgba(0, 0, 0, 0.1)",
  left: 0,
  width: "100%",
  zIndex: NavBarZIndex - StackingLevel.LOW,
  backgroundColor: "white",
});

const MobileNavigationButtonRow = styled("div")({
  display: "flex",
  flexDirection: "row",
  marginBottom: "15px",
  marginTop: "10px",
  justifyContent: "center",
  width: "100%",
  zIndex: NavBarZIndex - StackingLevel.LOW,
});

const ReferencesButton = styled(Button)(({ theme }) => ({
  borderColor: KahunColorMap.Accent,
  borderRadius: "56px",
  height: "40px",
  color: KahunColorMap.Accent,
  textTransform: "none",
  fontSize: "17px",
  fontWeight: 500,
  width: "250px",
  "&:hover,&:focus": {
    borderColor: KahunColorMap.Accent,
  },

  "&.MuiButton-root.Mui-disabled": {
    color: "white",
    opacity: 0.5,
  },
}));

const MobileContainer = styled("div")({});

const BackArrowContainer = styled("div")({
  position: "absolute",
  left: "0px",
  top: "0px",
  zIndex: NavBarZIndex,
});

const ClinicalReasoningIcon = styled("div")({
  position: "absolute",
  left: "10px",
  top: "8px",
});

//endregion [[ Styles ]]

//region [[ Props ]]
interface PatientDiseaseContainerProps {
  fWithCrs: DiseaseCluster;
  isMiniView?: boolean;
  changeTab?: (tabIndex: number) => void;
}

//end region [[ Props ]]
export const PatientDiseaseContainer = ({
  fWithCrs,
  isMiniView,
  changeTab,
}: PatientDiseaseContainerProps) => {
  const { featureId: paramsFeatureId } = useParams<{ featureId: string }>();
  const history = useHistory();
  const { patientCase } = usePatientCase();

  const { isMobile } = useScreenSize();
  const graphActions = useRef<GraphAreaActions>();
  useStoredPatientCase();
  const queryParams = useQueryParams();
  const resourcesOpen = queryParams.get("resources") === "true";

  const [selectedCR, setSelectedCR] = useState<DDClinicalReasoning>();

  const {
    featuresSections,
    ddData,
    crs,
    loading: ddLoading,
  } = useDiseasesSections();

  const featureId = paramsFeatureId;

  const diseaseCluster = featuresSections
    .flatMap((section) => section.elements)
    .find((sfwc) => sfwc.qf.id === featureId);

  const currentSelectedDisease =
    fWithCrs?.qf || selectedCR?.disease[0] || diseaseCluster?.qf;

  const graphFindings = useMemo(
    () => patientCaseByPresentationType(patientCase),
    [patientCase],
  );

  let { data: graphData, loading } = usePatientGraph(
    currentSelectedDisease,
    graphFindings,
  );

  // compute total articles found
  const graph = graphData?.graph;

  const graphSelection = useGraphDetailSelection({
    graph: graph,
  });

  const findingPredictiveCalculator = useCalculateFindingPredictives(crs);

  const defaultCR = fWithCrs?.crs[0] || selectedCR || diseaseCluster?.crs[0];
  const supportingFinding = useMemo(
    () =>
      (findingPredictiveCalculator
        ? findingPredictiveCalculator(defaultCR)
        : []
      )
        .filter((fs) => fs.strength > 0)
        .sort((b, a) => a.strength - b.strength)
        .map((sf) => sf.qf),
    [findingPredictiveCalculator, defaultCR],
  );

  const goBack = useCallback(
    (path: string, category: CATEGORY) => {
      evt({
        category: category,
        action: ACTION.BACK,
      });

      queryParams.delete("resources");
      history.push({
        pathname: `${path}`,
        search: queryParams.toString(),
      });
    },
    [history, queryParams],
  );

  const onSelectFeature = useCallback(
    (qf: QualifiedFeature) => {
      evt({
        category: CATEGORY.AGENT_DISEASE_CARD,
        action: ACTION.SELECT_FEATURE,
        label: qf.name,
      });
      graphActions?.current?.focusFinding(hashQF(qf));
    },
    [graphActions],
  );

  if (ddLoading) return <ThreeDotsLoader />;

  if (!ddData) return <div />;

  const { count: articleCount } = getArticles(graphSelection);

  const onBackClick = (event?) => {
    goBack(history.location.pathname, CATEGORY.AGENT_DISEASE_CARD);
  };

  return isMobile || isMiniView ? (
    <MobileContainer>
      {resourcesOpen && <ReferencesView graphSelection={graphSelection} />}
      {!resourcesOpen && (
        <GraphContainerMobile>
          <Graph
            loading={loading}
            graphData={graphData}
            graphSelection={graphSelection}
            graphActions={graphActions}
            supportingFinding={supportingFinding}
            onSelectFeature={onSelectFeature}
            isMiniView={true}
          />
        </GraphContainerMobile>
      )}
      {isMobile && (
        <MobileBottomArea>
          {resourcesOpen && (
            <BackArrowContainer>
              <BackButton
                onClick={onBackClick}
                color={KahunColorMap.DefaultLight}
              />
            </BackArrowContainer>
          )}

          {!resourcesOpen && graphSelection.hasEdgeSelect && (
            <GraphSelectionSummaryMobile selection={graphSelection} />
          )}
          <MobileNavigationButtonRow>
            <ReferencesButton
              variant={"outlined"}
              onClick={() => {
                if (resourcesOpen) {
                  onBackClick();
                  //changeTab(3);
                } else {
                  history.push(history.location.pathname + "?resources=true");
                }
              }}
            >
              <ClinicalReasoningIcon>
                <img src={ClinicalReasoningIconSVG} alt={"clinicalReasoning"} />
              </ClinicalReasoningIcon>
              {articleCount} References
            </ReferencesButton>
          </MobileNavigationButtonRow>
        </MobileBottomArea>
      )}
    </MobileContainer>
  ) : (
    <DesktopExpandView>
      {!isMobile && (
        <LeftPanelView
          {...{
            articleCount,
            diseaseCluster,
            findingPredictiveCalculator,
            graphSelection,
            graphActions,
            selectedCR,
            setSelectedCR,
            onSelectFeature,
            resourcesOpen,
          }}
        />
      )}
      <Graph
        loading={loading}
        graphData={graphData}
        graphSelection={graphSelection}
        graphActions={graphActions}
        supportingFinding={supportingFinding}
        onSelectFeature={onSelectFeature}
      />
    </DesktopExpandView>
  );
};

function LeftPanelView(props: {
  diseaseCluster: DiseaseCluster;
  findingPredictiveCalculator: (cr: DDClinicalReasoning) => FindingStrength[];
  graphSelection: GraphDetailSelection;
  articleCount: number;
  selectedCR?: DDClinicalReasoning;
  setSelectedCR?: Dispatch<DDClinicalReasoning>;
  onSelectFeature: Dispatch<QualifiedFeature>;
  resourcesOpen?: boolean;
}) {
  const {
    diseaseCluster,
    findingPredictiveCalculator,
    graphSelection,
    articleCount,
    onSelectFeature,
    setSelectedCR,
    selectedCR,
    resourcesOpen,
  } = props;
  const [referenceOpen, setReferenceOpen] = useState(resourcesOpen);

  useEffect(() => {
    setReferenceOpen(resourcesOpen);

    // when the graph is selected, open the references
    if (graphSelection.selection.edge || graphSelection.selection.node) {
      setReferenceOpen(true);
    }
  }, [
    graphSelection.selection.edge,
    graphSelection.selection.node,
    resourcesOpen,
  ]);

  const onClose = useCallback(() => {
    setReferenceOpen(false);
    evt({
      category: CATEGORY.AGENT_DISEASE_CARD,
      action: ACTION.CLOSE_RESOURCES,
    });
  }, []);

  const onGotoReferences = useCallback(() => {
    evt({
      category: CATEGORY.AGENT_DISEASE_CARD,
      action: ACTION.OPEN_RESOURCES,
    });
    setReferenceOpen(true);
  }, []);

  return (
    <LeftCardContainer elevation={0}>
      {referenceOpen ? (
        <ReferencesView {...{ onClose, graphSelection }} />
      ) : (
        <PatientDiseaseInfo
          {...{
            diseaseCluster,
            findingPredictiveCalculator,
            articleCount,
            onGotoReferences,
            onSelect: onSelectFeature,
            setSelectedCR,
            selectedCR,
          }}
        />
      )}
    </LeftCardContainer>
  );
}
